import { Injectable, Injector } from '@angular/core';
import moment from 'moment';
import * as Push from 'push.js';
import { AppConsts } from 'src/shared/AppConsts';
import { AppComponentBase } from 'src/shared/common/app-component-base';
import { EntityDtoOfGuid, NotificationServiceProxy } from 'src/shared/service-proxies/service-proxies';

import { NotificationSettingsModalComponent } from './notification-settings-modal.component';

export interface IFormattedUserNotification {
  userNotificationId: string;
  text: string;
  time: string;
  creationTime: Date;
  icon: string;
  state: string;
  data: any;
  url: string;
  isUnread: boolean;
}

@Injectable()
export class UserNotificationHelper extends AppComponentBase {
  settingsModal: NotificationSettingsModalComponent;

  constructor(injector: Injector, private _notificationService: NotificationServiceProxy) {
    super(injector);
  }

  getUrl(userNotification: eaf.notifications.IUserNotification): string {
    switch (userNotification.notification.notificationName) {
      case 'App.NewUserRegistered':
        return '/app/admin/users?filterText=' + userNotification.notification.data.properties.emailAddress;
      case 'App.NewTenantRegistered':
        return '/app/admin/tenants?filterText=' + userNotification.notification.data.properties.tenancyName;
      case 'App.GdprDataPrepared':
        return (
          AppConsts.remoteServiceBaseUrl +
          '/File/DownloadBinaryFile?id=' +
          userNotification.notification.data.properties.binaryObjectId +
          '&contentType=application/zip&fileName=collectedData.zip'
        );
      //Add your custom notification names to navigate to a URL when user clicks to a notification.
    }

    //No url for this notification
    return '';
  }

  /* PUBLIC functions ******************************************/

  getUiIconBySeverity(severity: eaf.notifications.severity): string {
    switch (severity) {
      case eaf.notifications.severity.SUCCESS:
        return 'fa fa-check';
      case eaf.notifications.severity.WARN:
        return 'fa fa-exclamation-triangle';
      case eaf.notifications.severity.ERROR:
        return 'fa fa-bolt';
      case eaf.notifications.severity.FATAL:
        return 'fa fa-bomb';
      case eaf.notifications.severity.INFO:
      default:
        return 'fa fa-info';
    }
  }

  format(userNotification: eaf.notifications.IUserNotification, truncateText?: boolean): IFormattedUserNotification {
    const formatted: IFormattedUserNotification = {
      userNotificationId: userNotification.id,
      text: eaf.notifications.getFormattedMessageFromUserNotification(userNotification),
      time: moment(userNotification.notification.creationTime).format('YYYY-MM-DD HH:mm:ss'),
      creationTime: userNotification.notification.creationTime,
      icon: this.getUiIconBySeverity(userNotification.notification.severity),
      state: eaf.notifications.getUserNotificationStateAsString(userNotification.state),
      data: userNotification.notification.data,
      url: this.getUrl(userNotification),
      isUnread: userNotification.state === eaf.notifications.userNotificationState.UNREAD,
    };

    if (truncateText || truncateText === undefined) {
      formatted.text = eaf.utils.truncateStringWithPostfix(formatted.text, 40);
    }

    return formatted;
  }

  show(userNotification: eaf.notifications.IUserNotification): void {
    eaf.notifications.showUiNotifyForUserNotification(userNotification, {
      onclick: () => {
        const url = this.getUrl(userNotification);
        if (url) {
          location.href = url;
        }
      },
    });

    const checkAndRequestNotificationPermission = (): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (!('Notification' in window)) {
          console.log('Este navegador não suporta notificações desktop');
          reject('not-supported');
          return;
        }

        if (Notification.permission === 'granted') {
          resolve('granted');
          return;
        }

        if (Notification.permission === 'denied') {
          console.log('Permissão para notificações foi negada pelo usuário');
          reject('denied');
          return;
        }

        Notification.requestPermission()
          .then(permission => {
            if (permission === 'granted') {
              resolve('granted');
            } else {
              reject(permission);
            }
          })
          .catch(error => {
            console.error('Erro ao solicitar permissão:', error);
            reject('error');
          });
      });
    };

    checkAndRequestNotificationPermission()
      .then(() => {
        Push.default.create('OCCManager', {
          body: this.format(userNotification).text,
          icon: eaf.appPath + 'assets/common/images/eaf/eaf.png',
          timeout: 8000,
          onClick: function () {
            window.focus();
            this.close();
          },
        });
      })
      .catch(status => {
        if (status === 'denied') {
          console.log('Notificações desktop estão bloqueadas pelo usuário');
        } else if (status === 'not-supported') {
          console.log('Notificações desktop não são suportadas neste navegador');
        }
      });
  }

  setAllAsRead(callback?: () => void): void {
    this._notificationService.setAllNotificationsAsRead().subscribe(() => {
      eaf.event.trigger('app.notifications.refresh');
      if (callback) {
        callback();
      }
    });
  }

  setAsRead(userNotificationId: string, callback?: (userNotificationId: string) => void): void {
    const input = new EntityDtoOfGuid();
    input.id = userNotificationId;
    this._notificationService.setNotificationAsRead(input).subscribe(() => {
      eaf.event.trigger('app.notifications.read', userNotificationId);
      if (callback) {
        callback(userNotificationId);
      }
    });
  }

  openSettingsModal(): void {
    this.settingsModal.show();
  }
}
